import React, { useState, useCallback, useEffect } from "react";
import {
  Box, Typography, CircularProgress, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DownloadIcon from "@mui/icons-material/Download";
import { axiosConfig } from "../../axios/axiosConfig";
import toast from "react-hot-toast";

const fmt = (dateStr) =>
  dateStr
    ? new Date(dateStr).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "2-digit",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

const FailedPaymentsPage = () => {
  const [fromDate, setFromDate]       = useState();
  const [toDate, setToDate]           = useState();
  const [payments, setPayments]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: `/payment/get-failed-payments/?from_date=${fromDate}&to_date=${toDate}`,
      });
      const data = res.data?.data ?? res.data ?? [];
      setPayments(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed to fetch");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    if (fromDate && toDate) fetchPayments();
  }, [fromDate, toDate, fetchPayments]);

  const downloadExcel = useCallback(async () => {
    if (!fromDate || !toDate) { toast.error("Please select both dates"); return; }
    setDownloading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: `/payment/get-failed-payments/?from_date=${fromDate}&to_date=${toDate}&download=true`,
        responseType: "blob",
      });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      const filename = match?.[1]?.replace(/['"]/g, "") || "failed-payments";

      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/vnd.ms-excel" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", filename);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  }, [fromDate, toDate]);

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>

      {/* Header */}
      <Box sx={{ background: "#04343a", borderRadius: "12px", px: 3, py: 2, mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 4px 20px rgba(4,52,58,0.3)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ErrorOutlineIcon sx={{ color: "#eef2f7", fontSize: 26 }} />
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>Failed Payments</Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>Select date range to view and download failed transactions</Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", p: 2.5, mb: 3, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <TextField
          label="From" type="date" size="small" value={fromDate ?? ""}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }}
        />
        <TextField
          label="To" type="date" size="small" value={toDate ?? ""}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }}
        />
        <Button
          variant="contained"
          startIcon={downloading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <DownloadIcon />}
          onClick={downloadExcel}
          disabled={downloading || !fromDate || !toDate}
          sx={{ background: "#016551", textTransform: "none", fontWeight: 600, borderRadius: "8px", px: 3, boxShadow: "0 4px 12px rgba(1,101,81,0.3)", "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" } }}
        >
          {downloading ? "Downloading…" : "Download"}
        </Button>
        {payments.length > 0 && (
          <Typography sx={{ fontSize: 13, color: "#64748b", ml: "auto" }}>
            <strong style={{ color: "#04343a" }}>{payments.length}</strong> records found
          </Typography>
        )}
      </Box>

      {/* Table */}
      <Box sx={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <Box sx={{ background: "#04343a", px: 3, py: 1.5 }}>
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>Failed Transactions</Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#016551" }} size={36} />
          </Box>
        ) : !fromDate || !toDate ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <ErrorOutlineIcon sx={{ fontSize: 44, color: "#cbd5e1", mb: 1 }} />
            <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>Select From and To date to load data</Typography>
          </Box>
        ) : payments.length === 0 ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>No failed payments in this date range</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#f8fafc" }}>
                  {["#", "User", "Phone", "Amount", "Status", "Payment Date"].map((h) => (
                    <TableCell key={h} sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", py: 1.5, borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((p, i) => (
                  <TableRow key={p.id ?? i} sx={{ "&:hover": { background: "#f8fafc" }, transition: "background 0.15s" }}>
                    <TableCell sx={{ fontSize: 12, color: "#94a3b8", py: 1.5 }}>{i + 1}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#04343a" }}>{p.userid__username ?? "—"}</Typography>
                      <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{p.userid__email ?? ""}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: "#374151", py: 1.5 }}>{p.userid__phoneno ?? "—"}</TableCell>
                    <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "#dc2626", py: 1.5 }}>
                      {p.amount != null ? `₹${p.amount}` : "—"}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Chip
                        label={p.payment_status ? "Success" : "Failed"}
                        size="small"
                        sx={{ fontSize: 11, height: 22, fontWeight: 700, borderRadius: "6px", background: p.payment_status ? "#dcfce7" : "#fee2e2", color: p.payment_status ? "#16a34a" : "#dc2626" }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: "#94a3b8", py: 1.5, whiteSpace: "nowrap" }}>{fmt(p.payment_date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default FailedPaymentsPage;
